// ============================================================================
// ui/modal.js — accessible, Promise-based modal (crossover §6 a11y upgrade)
// ============================================================================
// The crossover flags the Room Designer's dialogs as having an accessibility
// gap (no focus trap / no return-focus). V2 fixes it once, here:
//   • role="dialog" + aria-modal="true" + aria-labelledby
//   • focus trap (Tab / Shift+Tab cycle within the dialog)
//   • Esc closes; click on the scrim closes
//   • focus returns to the element that opened it
//   • body scroll locked while open
// `await openModal({...})` resolves with the chosen action id, or null on
// dismiss. Reduced-motion respected via CSS (no JS animation here).
// ============================================================================

import { h } from './dom.js';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),' +
  ' textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * @param {object} opts
 * @param {HTMLElement} opts.mount        - element to append the modal into (the .weight-designer root).
 * @param {string} opts.title
 * @param {HTMLElement|string} [opts.body]
 * @param {Array<{id:string,label:string,variant?:'primary'|'ghost'|'danger'}>} [opts.actions]
 * @returns {Promise<string|null>}
 */
/* `variant` names an OPTIONAL look for a caller that needs one, as a modifier class on the dialog
   (`wd-modal--<variant>`). Added 2026-08-09 for the quote step's enlarge preview, which Ryan asked to
   be glass with a centred action — a presentational choice that belongs to THAT dialog and must not
   quietly restyle every confirm and error modal that shares this component. One string in, one class
   out; the stylesheet owns what it means. */
export function openModal({ mount, title, body, variant, closeX = false, actions = [{ id: 'ok', label: 'OK', variant: 'primary' }] }) {
  const opener = typeof document !== 'undefined' ? document.activeElement : null;
  const titleId = `wd-modal-title-${Math.random().toString(36).slice(2, 8)}`;

  /* WD 483b: `variant` may name SEVERAL looks, space-separated ('glass tapfix') - the tap-fix editor is the
     glass dialog plus its own sizing. One modifier class per word; `glass` still switches the pill buttons. */
  const variants = String(variant || '').split(/\s+/).filter(Boolean);
  return new Promise((resolve) => {
    let closed = false;
    /* WD 505 (Ryan 11-02: "the 3 burger line button in the top right corner doesn't blur out like the rest of the
       background, and it still becomes clickable"). The phone's hamburger lives on <body> at z 2147483001, above every
       scrim, so the dialog says it is open on <body> and the stylesheet stands the burger down: blurred and inert behind
       a card. ⛔ NOT behind the full-height `sheet`: WD 503 keeps the burger over it on purpose, as on Finalize. Counted,
       so a dialog opened over another does not bring the burger back when the top one closes. */
    const sheetLike = variants.includes('sheet');
    const setOpenMarks = (d) => {
      const b = document.body; if (!b || sheetLike) return;
      const n = Math.max(0, (Number(b.dataset.wdModals) || 0) + d);
      if (n) b.dataset.wdModals = String(n); else delete b.dataset.wdModals;
      b.classList.toggle('wd-modal-open', n > 0);
    };

    const dialog = h(
      'div',
      {
        class: `wd-modal${variants.map((v) => ` wd-modal--${v}`).join('')}`,
        role: 'dialog',
        'aria-modal': 'true',
        'aria-labelledby': titleId,
      },
      h('h2', { class: 'wd-modal-title', id: titleId }, title),
      /* ⚠ AN ✕ INSTEAD OF A BUTTON ROW, FOR A DIALOG THAT ONLY DISMISSES (Ryan 2026-08-09: *"make the
         close button an 'x' in the top right corner instead. No need for the big blue button."*)
           Offered as an option rather than applied to every modal, and the test is what the dialog
         ASKS: a confirm needs a full-width, unmissable action because a decision is being taken, and
         an ✕ would make "no" the easy path by accident. A preview takes no decision — the only exit
         is out — so a corner ✕ is the honest control and the blue button was overstating it.
           It resolves `null`, exactly as the scrim tap and Escape already do, so every dismissal
         route on this dialog returns the same thing and no caller has to tell them apart. */
      closeX
        ? h('button', {
          type: 'button',
          class: 'wd-modal-x',
          'aria-label': 'Close',
          title: 'Close',
          onClick: () => close(null),
        }, '✕')
        : null,
      body ? h('div', { class: 'wd-modal-body' }, typeof body === 'string' ? h('p', {}, body) : body) : null,
      // An empty `actions` list emits no row at all — otherwise the flex gap and margin below the
      // body would reserve space for a button that is not there.
      actions.length === 0 ? null : h(
        'div',
        { class: 'wd-modal-actions' },
        /* ⭐ THE GLASS DIALOG GETS THE APP'S GLASS BUTTON (Ryan 2026-08-10: *"have it be more
           on-brand"*). `.wd-gate-btn` is the lit-glass pill the gate's own CTAs wear — a bright top
           edge, light pooling down the face, and the left-to-right fill wipe on hover. It is NOT a
           gate-only class (the product-step pills already borrow it), so this is reuse rather than a
           second button treatment invented for dialogs.
           ⚠ THE TWO SPANS ARE THE RIG, not decoration: `__fill` is the wipe layer and `__t` is the
           label sitting above it. A bare text node would still get the bevel and silently lose the
           hover. Both are added only for the glass variant, so every other dialog in the tool keeps
           the plain button it has always had. */
        actions.map((a) => {
          const glass = variants.includes('glass');
          return h(
            'button',
            {
              type: 'button',
              class: `wd-btn wd-btn--${a.variant || 'ghost'}${glass ? ' wd-gate-btn' : ''}`,
              onClick: () => close(a.id),
            },
            ...(glass
              ? [h('span', { class: 'wd-gate-btn__fill', 'aria-hidden': 'true' }),
                h('span', { class: 'wd-gate-btn__t' }, a.label)]
              : [a.label]),
          );
        }),
      ),
    );

    const scrim = h(
      'div',
      { class: 'wd-modal-scrim', onClick: (e) => e.target === scrim && close(null) },
      dialog,
    );

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(null);
        return;
      }
      if (e.key !== 'Tab') return;
      const nodes = Array.from(dialog.querySelectorAll(FOCUSABLE)).filter((n) => n.offsetParent !== null);
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function close(result) {
      if (closed) return;
      closed = true;
      document.removeEventListener('keydown', onKeydown, true);
      /* WD 503: the phone's full-height sheet (`sheet` variant) LEAVES the way it came, sliding down - the answer is
         returned at once, the node goes when the slide ends (or at the cap, if no animation runs). */
      if (variants.includes('sheet') && !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
        scrim.classList.add('is-leaving');
        let gone = false;
        const drop = () => { if (!gone) { gone = true; scrim.remove(); } };
        dialog.addEventListener('animationend', drop, { once: true });
        setTimeout(drop, 700);
      } else {
        scrim.remove();
      }
      document.body.style.overflow = prevOverflow;
      setOpenMarks(-1);
      if (opener && typeof opener.focus === 'function') opener.focus();
      resolve(result);
    }

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setOpenMarks(1);
    document.addEventListener('keydown', onKeydown, true);
    mount.append(scrim);

    // Initial focus lands inside the dialog (focus trap starts here).
    const firstFocusable = dialog.querySelector(FOCUSABLE);
    (firstFocusable || dialog).focus();
  });
}
